/**
 * The gateway's contract, as this app needs it.
 *
 * Written from scratch rather than shared with `fabric_ui`, deliberately. The builder's own run
 * model covers pauses, retries, single-node runs, human input and historical replay; a demo needs
 * none of that, and carrying it would couple two repos so that a change to the builder's internals
 * could break a customer-facing app.
 *
 * ⚠️ What IS shared is the wire format, and that is not ours to choose — it is Dify's. Everything
 * below was read off a live engine, not inferred.
 */

/** One declared input, exactly as the engine describes it. */
export interface InputSpec {
  variable: string
  label: string
  type: InputType
  required: boolean
  /** Characters for text, ⚠️ FILES for `file-list` — the engine reuses the field for both. */
  max_length?: number
  options?: string[]
  /** `document` | `image` | `audio` | `video` | `custom` */
  allowed_file_types?: string[]
  /** With the dot: `.pdf`. Feeds the file picker's `accept`. */
  allowed_file_extensions?: string[]
  allowed_file_upload_methods?: string[]
  /** Draft-7, and ⚠️ genuinely ENFORCED at the boundary — `start_node.py:74`. */
  json_schema?: Record<string, unknown>
}

export type InputType =
  | 'text-input'
  | 'paragraph'
  | 'select'
  | 'number'
  | 'checkbox'
  | 'json_object'
  | 'file'
  | 'file-list'

/**
 * `GET /api/form`.
 *
 * ⚠️ `user_input_form` arrives in the engine's OLD STRUCTURE: a list of single-key objects keyed by
 * the variable's own type — `[{ "file": {…} }, { "select": {…} }]`. That is literally
 * `{variable["type"]: variable}` (`models/workflow.py:531`), so the inner object is the unmodified
 * start-node variable and nothing is lost. Verified against a live engine, 2026-09-01.
 */
export interface FormResponse {
  user_input_form?: Record<string, InputSpec>[]
}

export function specsFrom(form: FormResponse): InputSpec[] {
  const out: InputSpec[] = []
  for (const wrapper of form.user_input_form ?? []) {
    // One key per entry, but read it structurally rather than trusting the count.
    for (const spec of Object.values(wrapper)) {
      if (spec && typeof spec.variable === 'string' && spec.variable !== '') out.push(spec)
    }
  }
  return out
}

/** What `POST /api/files` gives back. */
export interface UploadedFile {
  id: string
  name: string
  size: number
  extension: string
  mime_type: string
}

/**
 * The value a file input must carry into `inputs`.
 *
 * ⚠️ All three fields are required, and the truth table is not obvious — verified against the
 * engine by `fabric_ui` and reproduced here because getting it wrong fails INSIDE the run rather
 * than at the boundary:
 *
 *   a bare id string        → failed, "doc in input form must be a file"
 *   an object with no type  → failed, "File validation failed for file: doc.pdf"
 *   all three               → succeeded
 */
export interface FileValue {
  transfer_method: 'local_file'
  upload_file_id: string
  type: FileKind
}

export type FileKind = 'document' | 'image' | 'audio' | 'video' | 'custom'

/**
 * Classified by EXTENSION, because that is what the engine does
 * (`graphon/file/constants.py`) — not by the MIME type the browser guessed.
 */
const BY_EXTENSION: Record<string, FileKind> = {
  ...Object.fromEntries(
    ['txt', 'markdown', 'md', 'pdf', 'html', 'htm', 'xlsx', 'xls', 'doc', 'docx', 'csv', 'eml', 'msg', 'pptx', 'ppt', 'xml', 'epub'].map(
      (e) => [e, 'document' as FileKind],
    ),
  ),
  ...Object.fromEntries(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].map((e) => [e, 'image' as FileKind])),
  ...Object.fromEntries(['mp3', 'm4a', 'wav', 'amr', 'mpga'].map((e) => [e, 'audio' as FileKind])),
  ...Object.fromEntries(['mp4', 'mov', 'mpeg', 'webm'].map((e) => [e, 'video' as FileKind])),
}

export function fileKindOf(extension: string): FileKind {
  return BY_EXTENSION[extension.replace(/^\./, '').toLowerCase()] ?? 'custom'
}

export function toFileValue(file: UploadedFile): FileValue {
  return { transfer_method: 'local_file', upload_file_id: file.id, type: fileKindOf(file.extension) }
}
